import React, { useState, useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import Axios from '../../Axios';
import { Urls } from '../../Urls';
import styles from './BenchmarkHistory.module.css';
import { PiTruckFill } from "react-icons/pi";

const formatRate = (val) => {
    if (typeof val === 'number') {
        return `₹ ${val.toLocaleString('en-IN')}`;
    }
    return null; // will show "Under Validation" with muted style
};

const BenchmarkHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const limit = 5;

    useEffect(() => {
        mixpanel.track("User has viewed benchmark history", {
            ApplicationName: `${Urls.mixpanelappname}`,
            ScreenName: "LoRRI-Benchmark",
            ModuleName: "benchmark_history",
            ActivityLog: "userViewedBenchmarkHistory",
            Data: "None",
            Definition: "User has viewed benchmark history",
            ActivityType: "page_view",
            UserName: `${localStorage.getItem("userdetails")}`,
            UserEmail: `${localStorage.getItem("useremail")}`,
            CompanyName: `${localStorage.getItem("companyname")}`,
        });
        setLoading(true);
        Axios.get(Urls.user_data)
            .then(res => {
                if (res.data && res.data.policy && res.data.policy.benchmarknow && res.data.policy.benchmarknow.history) {
                    const sorted = [...res.data.policy.benchmarknow.history].sort(
                        (a, b) => new Date(b.session_created_on) - new Date(a.session_created_on)
                    );
                    setHistory(sorted);
                } else {
                    setHistory([]);
                }
            })
            .catch(() => setHistory([]))
            .finally(() => setLoading(false));
    }, []);

    const handlePrevPage = () => {
        mixpanel.track("User has navigated benchmark history pagination", {
            ApplicationName: `${Urls.mixpanelappname}`,
            ScreenName: "LoRRI-Benchmark",
            ModuleName: "benchmark_history",
            ActivityLog: "userNavigatedHistoryPagination",
            Data: `direction: prev, page: ${page}`,
            Definition: "User has navigated benchmark history pagination",
            ActivityType: "event_view",
            UserName: `${localStorage.getItem("userdetails")}`,
            UserEmail: `${localStorage.getItem("useremail")}`,
            CompanyName: `${localStorage.getItem("companyname")}`,
        });
        setPage(p => p - 1);
    };

    const handleNextPage = () => {
        mixpanel.track("User has navigated benchmark history pagination", {
            ApplicationName: `${Urls.mixpanelappname}`,
            ScreenName: "LoRRI-Benchmark",
            ModuleName: "benchmark_history",
            ActivityLog: "userNavigatedHistoryPagination",
            Data: `direction: next, page: ${page + 2}`,
            Definition: "User has navigated benchmark history pagination",
            ActivityType: "event_view",
            UserName: `${localStorage.getItem("userdetails")}`,
            UserEmail: `${localStorage.getItem("useremail")}`,
            CompanyName: `${localStorage.getItem("companyname")}`,
        });
        setPage(p => p + 1);
    };

    const totalPages = Math.ceil(history.length / limit);
    const currentItems = history.slice(page * limit, (page + 1) * limit);

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <div className={styles.spinner} />
                Loading history…
            </div>
        );
    }

    if (history.length === 0) {
        return (
            <div className={styles.emptyState}>
                <span className={styles.emptyIcon}>📭</span>
                No benchmark history found.
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.cardList}>
                {currentItems.map((session, idx) => {
                    const network = session.network || [];
                    const pageOffset = history
                        .slice(0, page * limit)
                        .reduce((sum, s) => sum + (s.network || []).length, 0);
                    const priorLanes = currentItems
                        .slice(0, idx)
                        .reduce((sum, s) => sum + (s.network || []).length, 0);
                    return network.map((lane, laneIdx) => {
                        const srNo = pageOffset + priorLanes + laneIdx + 1;
                        const dashboard = lane.benchmarknow_analysis?.dashboard || [];
                        const minObj    = dashboard.find(d => d.label === 'Minimum Rate');
                        const medianObj = dashboard.find(d => d.label === 'Median Rate');
                        const maxObj    = dashboard.find(d => d.label === 'Maximum Rate');

                        const hasData = dashboard.length > 0;

                        const shortName = (name) => {
                            if (!name) return '-';
                            const parts = name.split(',').map(s => s.trim()).filter(Boolean);
                            return parts.slice(0, 2).join(', ');
                        };

                        return (
                            <div className={styles.card} key={`${session.session_id}-${laneIdx}`}>
                                {/* Left Section: Route & Truck Info */}
                                <div className={styles.leftSection}>
                                    <div className={styles.routeRow}>
                                        <span className={styles.srNo}>{srNo}.</span>
                                        <span className={styles.cityName}>{shortName(lane.origin_name)}</span>
                                        <span className={styles.arrow}>→</span>
                                        <span className={styles.cityName}>{shortName(lane.destination_name)}</span>
                                    </div>
                                    <div className={styles.truckRow}>
                                        <div className={styles.truckPill}>
                                            <span className={styles.truckIcon}><PiTruckFill /></span>
                                            {lane.truck_type || '—'}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Section: Median Rate Chip */}
                                <div className={styles.rightSection}>
                                    {hasData ? (
                                        <div className={`${styles.rateChip} ${styles.rateChipMedian}`}>
                                            <div className={styles.rateLabel}>Median Rate</div>
                                            {formatRate(medianObj?.value) ? (
                                                <div className={styles.rateValue}>{formatRate(medianObj?.value)}</div>
                                            ) : (
                                                <div className={styles.rateValueMuted}>Under Validation</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={styles.noAnalysis}>No rate data available</div>
                                    )}
                                </div>
                            </div>
                        );
                    });
                })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button
                        className={styles.pageBtn}
                        disabled={page === 0}
                        onClick={handlePrevPage}
                    >
                        ← Previous
                    </button>
                    <span className={styles.pageInfo}>Page {page + 1} of {totalPages}</span>
                    <button
                        className={styles.pageBtn}
                        disabled={page + 1 >= totalPages}
                        onClick={handleNextPage}
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};

export default BenchmarkHistory;
